const STORAGE_KEY = 'auth.otpAttempts'
export const RESEND_BLOCK_DURATION_MS = 15 * 60 * 1000
// initial signin counts as 1; user is allowed 3 resends => total 4 attempts
export const MAX_OTP_ATTEMPTS = 4
// after this many ms with no new attempt, the counter naturally resets
const ATTEMPT_WINDOW_MS = RESEND_BLOCK_DURATION_MS

type Entry = {
  count: number
  lastAttemptAt: number
  blockedUntil?: number
}
type AttemptsMap = Record<string, Entry>

function readAll(): AttemptsMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeAll(map: AttemptsMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // storage unavailable (private mode, quota) — silently ignore
  }
}

function isEntryActive(entry: Entry | undefined, now: number): boolean {
  if (!entry) return false
  if (entry.blockedUntil && entry.blockedUntil > now) return true
  return now - entry.lastAttemptAt < ATTEMPT_WINDOW_MS
}

function pruneExpired(map: AttemptsMap, now: number): AttemptsMap {
  return Object.entries(map).reduce<AttemptsMap>((acc, [phone, entry]) => {
    if (isEntryActive(entry, now)) acc[phone] = entry
    return acc
  }, {})
}

export function getBlockedUntil(phone: string): number | null {
  const now = Date.now()
  const map = readAll()
  const pruned = pruneExpired(map, now)
  if (Object.keys(pruned).length !== Object.keys(map).length) writeAll(pruned)
  const entry = pruned[phone]
  if (entry?.blockedUntil && entry.blockedUntil > now) return entry.blockedUntil
  return null
}

export function getAttemptCount(phone: string): number {
  const now = Date.now()
  const map = readAll()
  const entry = map[phone]
  if (!isEntryActive(entry, now)) return 0
  return entry?.count ?? 0
}

/**
 * Record one OTP send attempt for a phone. Returns the new state including
 * whether the user is now blocked.
 */
export function recordAttempt(phone: string): {
  count: number
  blockedUntil: number | null
} {
  const now = Date.now()
  const map = pruneExpired(readAll(), now)
  const previous = map[phone]
  const prevCount = isEntryActive(previous, now) ? (previous?.count ?? 0) : 0
  const nextCount = prevCount + 1
  const nextEntry: Entry = {
    count: nextCount,
    lastAttemptAt: now,
    blockedUntil: previous?.blockedUntil,
  }
  if (nextCount >= MAX_OTP_ATTEMPTS && !nextEntry.blockedUntil) {
    nextEntry.blockedUntil = now + RESEND_BLOCK_DURATION_MS
  }
  map[phone] = nextEntry
  writeAll(map)
  return {
    count: nextCount,
    blockedUntil: nextEntry.blockedUntil ?? null,
  }
}

export function clearAttempts(phone: string): void {
  const now = Date.now()
  const map = pruneExpired(readAll(), now)
  if (phone in map) {
    delete map[phone]
    writeAll(map)
  }
}
